// Superuser Help index  -  static shell, not a CMS.
// Published teaching articles are static React bodies in HelpPage (no CMS).
// Product lock: design/2026-08-08-help-kb-superuser-shell.html

export type HelpArticleStatus = "published" | "planned";

export interface HelpIndexEntry {
  id: string;
  section: string;
  title: string;
  status: HelpArticleStatus;
  /**
   * Planned entries carry the one-line reason they are not written yet. Shown on the stub so
   * a planned title is an honest promise with a stated blocker, never a silent gap.
   */
  plannedReason?: string;
}

/** Sidebar index order matches the design mock. */
export const HELP_INDEX: readonly HelpIndexEntry[] = [
  { id: "console-basics", section: "Getting around", title: "Console basics", status: "published" },
  {
    id: "roles-permissions",
    section: "Getting around",
    title: "Roles & permissions",
    status: "published",
  },
  {
    id: "cad-desk-basics",
    section: "Getting around",
    title: "Command Center desk",
    status: "planned",
    plannedReason:
      "The CAD desk is live and large (map layers, rails, focus, Jump). It deserves an accurate pass of its own, not a thin summary.",
  },
  {
    id: "drafts-then-publish",
    section: "Schedule",
    title: "Drafts then Publish in Builder",
    status: "published",
  },
  {
    id: "open-shifts-claiming",
    section: "Schedule",
    title: "Open shifts & claiming",
    status: "published",
  },
  {
    id: "template-colors-labels",
    section: "Schedule",
    title: "Template colors are labels",
    status: "published",
  },
  {
    id: "routes-checkpoints",
    section: "Patrols",
    title: "Routes & checkpoints",
    status: "published",
  },
  {
    id: "sos-to-incident",
    section: "Incidents",
    title: "From SOS to incident report",
    status: "published",
  },
  { id: "inviting-team", section: "Admin", title: "Inviting your team", status: "published" },
  {
    id: "alert-channel-policies",
    section: "Admin",
    title: "Alert channel policies",
    status: "published",
  },
  {
    id: "guard-app-access-request",
    section: "Admin",
    title: "Guard app access: requesting for a member",
    status: "planned",
    plannedReason:
      "Help is superuser-only today, so tenant admins cannot read it. Written for them when Help opens up; founders have the queue article under Platform.",
  },
  {
    id: "phone-staging-beta",
    section: "Platform",
    title: "Phone: staging and beta vs production",
    status: "published",
  },
  {
    id: "guard-app-access-queue",
    section: "Platform",
    title: "Guard app access: working the queue",
    status: "published",
  },
  {
    id: "support-sessions",
    section: "Platform",
    title: "Support sessions (act as a member)",
    status: "planned",
    plannedReason:
      "Act-as support sessions are live; the runbook needs the same care as the phone article before it publishes.",
  },
] as const;

export const DEFAULT_HELP_ARTICLE_ID = "drafts-then-publish";

export function helpPublishedCount(index: readonly HelpIndexEntry[] = HELP_INDEX): number {
  return index.filter((entry) => entry.status === "published").length;
}

export function helpPlannedCount(index: readonly HelpIndexEntry[] = HELP_INDEX): number {
  return index.filter((entry) => entry.status === "planned").length;
}

export function helpSections(
  index: readonly HelpIndexEntry[] = HELP_INDEX,
): { section: string; entries: HelpIndexEntry[] }[] {
  const order: string[] = [];
  const bySection = new Map<string, HelpIndexEntry[]>();
  for (const entry of index) {
    const list = bySection.get(entry.section);
    if (list === undefined) {
      order.push(entry.section);
      bySection.set(entry.section, [entry]);
    } else {
      list.push(entry);
    }
  }
  return order.map((section) => ({ section, entries: bySection.get(section) ?? [] }));
}

export function findHelpEntry(
  id: string,
  index: readonly HelpIndexEntry[] = HELP_INDEX,
): HelpIndexEntry | undefined {
  return index.find((entry) => entry.id === id);
}
